import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { OAuthService } from 'angular-oauth2-oidc';

@Injectable({
  providedIn: 'root'
})
export class CommonInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private readonly oauthService: OAuthService) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip token injection for certain URLs (e.g., auth endpoints)
    const skipUrls = ['/auth/', '/oauth/', '/token', '/realms/'];
    const shouldSkip = skipUrls.some(url => req.url.includes(url));
    
    if (shouldSkip) {
      return next.handle(req);
    }

    // Get the access token from OAuthService with proper validation
    const accessToken = this.getValidAccessToken();

    // If we have a valid access token, clone the request and add the Authorization header
    if (accessToken) {
      const authReq = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${accessToken}`)
      });
      
      return next.handle(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
          // If 401 error, try to refresh token and retry
          if (error.status === 401 && this.oauthService.getRefreshToken()) {
            return this.refreshAndRetry(req, next);
          }
          return throwError(() => error);
        })
      );
    }

    // If no valid token, proceed with the original request
    return next.handle(req);
  }

  /**
   * Gets a valid access token from OAuth service with fallback strategies
   */
  private getValidAccessToken(): string | null {
    // Strategy 1: Check if OAuth service has a valid access token
    if (this.oauthService.hasValidAccessToken()) {
      return this.oauthService.getAccessToken();
    }

    // Strategy 2: Try to get token directly from OAuth service (might be valid but not detected)
    const token = this.oauthService.getAccessToken();
    if (token && this.isTokenValid(token)) {
      return token;
    }

    // Strategy 3: Force OAuth service to re-read from storage
    try {
      this.oauthService.tryLoginImplicitFlow();
      if (this.oauthService.hasValidAccessToken()) {
        return this.oauthService.getAccessToken();
      }
    } catch (error) {
      // Silently continue to next strategy
    }

    // Strategy 4: Direct localStorage access as last resort
    const directToken = localStorage.getItem('access_token');
    if (directToken && this.isTokenValid(directToken)) {
      return directToken;
    }

    return null;
  }

  /**
   * Basic token validation with small buffer for safety
   */
  private isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const bufferTime = 10000; // 10 seconds buffer to prevent edge cases
      return exp > (now + bufferTime);
    } catch (error) {
      return false;
    }
  }

  private refreshAndRetry(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If already refreshing, wait for the refresh to complete instead of throwing error
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          const authReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${token}`)
          });
          return next.handle(authReq);
        })
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);
    
    return from(this.oauthService.refreshToken()).pipe(
      switchMap(() => {
        this.isRefreshing = false;
        // Get the new access token after refresh
        const newToken = this.getValidAccessToken();
        if (newToken) {
          this.refreshTokenSubject.next(newToken);
          // Clone the original request with the new token
          const authReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${newToken}`)
          });
          return next.handle(authReq);
        } else {
          this.refreshTokenSubject.next(null);
          // No token after refresh, logout and throw error
          this.oauthService.logOut();
          return throwError(() => new Error('Token refresh failed - no access token received'));
        }
      }),
      catchError((refreshError) => {
        this.isRefreshing = false;
        this.refreshTokenSubject.next(null);
        // Refresh failed, logout user and propagate error
        this.oauthService.logOut();
        return throwError(() => refreshError instanceof Error ? refreshError : new Error(String(refreshError)));
      })
    );
  }
}
